import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../client';
import { useNotesStore } from '../../store/notesStore';
import type { ApiNote } from './types';

import { mapApiToNote } from '@/api/notes/noteUtils';

export function useLoadNotes(sort?: string, tag?: string) {
  const setNotes = useNotesStore((s) => s.setNotes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    // cancel previous in-flight request
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (sort) params.sort = sort;
      if (tag) params.tag = tag;

      const data = await apiGet<{ notes: ApiNote[] }>(
        '/app/v1/note',
        Object.keys(params).length ? params : undefined
      );
      setNotes((data.notes || []).map(mapApiToNote));
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Failed to load notes');
    } finally {
      if (ctrlRef.current === ctrl) ctrlRef.current = null;
      setLoading(false);
    }
  }, [setNotes, sort, tag]);

  useEffect(() => {
    void refetch();
    return () => ctrlRef.current?.abort();
  }, [refetch]);

  return { loading, error, refetch };
}
